import styles from "./styles.module.scss";
import { FC, Props } from "./typings";
import Input from "components/Input";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useState } from "react";
import Button from "components/Button";
import CopyIcon from "assets/icons/copy.svg?react";
import { useAuth } from "react-oidc-context";
import HollowButton from "components/HollowButton";
import activationService from "api/activationService";
import { useDropzone } from "react-dropzone";
import classNames from "classnames";
import Spinner from "components/Spinner";
import { getErrorMessage } from "utils/getErrorMessage";
import { IMobywatelQRCodeResponse } from "dto/Activation/IMobywatelQRCodeResponse";
import { ENotificationType, notification } from "utils/notification";
import { QRCodeCanvas } from "qrcode.react";
import ReactDOM from "react-dom";
import { SignalRContext } from "./../../../App";
import { HubCallbacksNames, IUserIdentified } from "api/hub";
import SignatureIcon from "assets/icons/signature.svg?react";
import FilesBg from "assets/icons/filesandfolder.svg?react";
import MobywatelIcon from "assets/icons/pi.svg?react";
import { getUserClaim } from "utils/getUserClaim";
import { Claims } from "dto/User/claims";
import { Tooltip } from "@mui/material";
import { useMediaQuery } from "usehooks-ts";

export enum IdentityMethod {
  Signature = "signature",
  LoginGov = "loginGov",
  Mobywatel = "mObywatel",
}

export enum IdentityStep {
  DownloadStatement = "downloadStatement",
  UploadStatement = "uploadStatement",
  MObywatelQRCode = "mobywatelQRCode",
  Success = "success",
}

const Identity: FC<Props> = ({ nextStep }) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const isMobile = useMediaQuery("(max-width: 767px)");

  SignalRContext.useSignalREffect(
    HubCallbacksNames.UserIdentified,
    (message: IUserIdentified) => {
      if (message.success) {
        auth.revokeTokens();
        auth.signinSilent();
        setErrorMessage("");
        setStep(IdentityStep.Success);
      } else {
        notification({
          title: t("identity.verificationFailed"),
          text: message.message,
          type: ENotificationType.ERROR,
        });
      }
    },
    [],
  );

  const [personalData, setPersonalData] = useState<{
    firstName: string;
    lastName: Nullable<string>;
    pesel: Nullable<string>;
  }>({
    firstName: getUserClaim(auth.user!, Claims.Eidas_FirstName),
    lastName: getUserClaim(auth.user!, Claims.Eidas_FamilyName),
    pesel: getUserClaim(auth.user!, Claims.Eidas_PersonIdentifier),
  });

  const [method, setMethod] = useState<IdentityMethod | null>(null);
  const [step, setStep] = useState<IdentityStep | null>(null);
  const [allowBack, setAllowBack] = useState<boolean>(false);
  const [prevButtonState, setPrevButtonState] = useState({
    show: true,
    label: t("common.back"),
    disabled: false,
  });

  const [nextButtonState, setNextButtonState] = useState({
    show: false,
    label: "",
    disabled: false,
  });
  const [statement, setStatement] = useState<File | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>(null);
  const [qrCodeData, setQRCodeData] = useState<string>("");
  const [key, setKey] = useState<string>("");

  const { mutate: getStatement } =
    activationService.useGetStatement();

  const { mutate: uploadStatement, isLoading: isUploadingStatement } =
    activationService.useUploadStatement();

  const { mutate: getMobywatelQRCode, isLoading: isLoadingQRCode } =
    activationService.useGetMObywatelQRcode();

  const nextStepInternal = () => {
    switch (step) {
      case IdentityStep.DownloadStatement:
        setAllowBack(true);
        downloadStatement();
        break;
      case IdentityStep.UploadStatement:
        setAllowBack(true);
        break;
      case IdentityStep.Success:
        setAllowBack(false);
        nextStep?.();
        break;
    }
  };

  const back = () => {
    switch (step) {
      case IdentityStep.DownloadStatement:
      case IdentityStep.MObywatelQRCode:
        setStep(null);
        setMethod(null);
        setAllowBack(false);
        break;
      case IdentityStep.UploadStatement:
        setStep(IdentityStep.DownloadStatement);
        break;
    }
  };

  const saveFile = async (blob: Blob) => {
    const a = document.createElement("a");
    a.download = "oswiadczenie_nota.pdf";
    a.href = URL.createObjectURL(blob);
    a.addEventListener("click", (e) => {
      setTimeout(() => URL.revokeObjectURL(a.href), 30 * 1000);
    });
    a.click();
  };

  const downloadStatement = () => {
    getStatement(
      {},
      {
        onSuccess(data) {
          saveFile(data.data);
          setStep(IdentityStep.UploadStatement);
        },
      },
    );
  };

  const onDrop = useCallback((acceptedFiles: any) => {
    setStatement(acceptedFiles[0]);
  }, []);

  const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } =
    useDropzone({
      accept: { "application/pdf": [".pdf"] },
      onDrop: onDrop,
      multiple: false,
    });

  const initMObywatelQRCode = () => {
    getMobywatelQRCode(
      {},
      {
        onSuccess(data) {
          const resp = data.data as IMobywatelQRCodeResponse;
          setQRCodeData(resp.Raw);
          setKey(resp.Raw);
        },
        onError(e: any) {
          notification({
            title: t("errors.somethingWentWrong"),
            text: getErrorMessage(e.response.data),
            type: ENotificationType.ERROR,
          });
        },
      },
    );
  };

  const handleOpenmObywatel = () => {
    if (key.length) {
      window.open(`mobywatel://app/institutions?qrCode=${key}`, "_blank");
    }
  };

  useEffect(() => {
    switch (method) {
      case IdentityMethod.Signature:
        setStep(IdentityStep.DownloadStatement);
        setNextButtonState({ ...nextButtonState, disabled: false, show: true });
        setPrevButtonState({
          ...prevButtonState,
          disabled: false,
          show: false,
        });
        setAllowBack(true);
        break;
      case IdentityMethod.Mobywatel:
        setStep(IdentityStep.MObywatelQRCode);
        setNextButtonState({
          ...nextButtonState,
          disabled: false,
          show: false,
        });
        setPrevButtonState({ ...prevButtonState, disabled: false, show: true });
        initMObywatelQRCode();
        setAllowBack(true);
        break;
      default:
        setNextButtonState({
          ...nextButtonState,
          disabled: false,
          show: false,
        });
        setPrevButtonState({
          ...prevButtonState,
          disabled: false,
          show: false,
        });
        setAllowBack(false);
    }
  }, [method]);

  useEffect(() => {
    switch (step) {
      case IdentityStep.DownloadStatement:
        setNextButtonState({
          ...nextButtonState,
          disabled: false,
          show: true,
          label: t("identity.downloadStatement"),
        });
        break;
      case IdentityStep.UploadStatement:
        setNextButtonState({
          ...nextButtonState,
          disabled: true,
          show: true,
          label: t("common.next"),
        });
        break;
      case IdentityStep.Success:
        setNextButtonState({
          ...nextButtonState,
          disabled: false,
          show: true,
          label: t("common.next"),
        });
        setPrevButtonState({
          ...prevButtonState,
          show: false,
        });
        setAllowBack(false);
        break;
    }
  }, [step]);

  useEffect(() => {
    if (statement) {
      uploadStatement(statement, {
        onSuccess: () => {
          auth.signinSilent();
          setErrorMessage("");
          setStep(IdentityStep.Success);
          setNextButtonState({
            ...nextButtonState,
            disabled: false,
            show: true,
            label: t("common.next"),
          });
        },
        onError: (e: any) => {
          setErrorMessage(getErrorMessage(e.response.data));
          setStatement(null);
        },
      });
    }
  }, [statement]);

  useEffect(() => {
    if (qrCodeData && document.getElementById("qrcode")) {
      ReactDOM.render(
        <QRCodeCanvas value={qrCodeData} size={!isMobile ? 256 : 180} />,
        document.getElementById("qrcode"),
      );
    }
  }, [qrCodeData]);

  useEffect(() => {
    if (auth.user) {
      setPersonalData((prevState) => ({
        ...prevState,
        firstName: getUserClaim(auth.user!, Claims.Eidas_FirstName),
        lastName: getUserClaim(auth.user!, Claims.Eidas_FamilyName),
        pesel: getUserClaim(auth.user!, Claims.Eidas_PersonIdentifier),
      }));
    }
  }, [auth.user]);

  return (
    <>
      {allowBack && (
        <div className={styles.backTop}>
          {" "}
          <HollowButton text={t("common.backArrow")} onClick={() => back?.()}></HollowButton>
        </div>
      )}
      <div className={styles.wrapper}>
        <div className={styles.header}>{t("identity.verifyTitle")}</div>
        {!method && (
          <>
            <div className={styles.subheader}>
              {t("identity.verifyMessage")}
            </div>
            <div className={styles.methods}>
              <div
                className={styles.method}
                onClick={() => setMethod(IdentityMethod.Mobywatel)}
              >
                <div className={styles.icon}>
                  <MobywatelIcon></MobywatelIcon>
                </div>
                {t("identity.mobywatelApp")}
              </div>
              <div
                className={styles.method}
                onClick={() => setMethod(IdentityMethod.Signature)}
              >
                <div className={styles.icon}>
                  <SignatureIcon></SignatureIcon>
                </div>
                {t("identity.qualifiedSignature")}
              </div>
            </div>
          </>
        )}

        {method === IdentityMethod.Signature &&
          step !== IdentityStep.Success && (
            <>
              <div className={styles.subheader}>
                {t("identity.signFileInstructions")}
              </div>
              {step === IdentityStep.DownloadStatement && (
                <div className={styles.movie}>
                  {/* <YouTube
                    videoId="ekKjpzKgzvc"
                    opts={{ height: 300, width: 500 }}
                  ></YouTube> */}
                </div>
              )}
              {step === IdentityStep.UploadStatement && (
                <>
                  <div
                    className={classNames(
                      styles.dropContainer,
                      isDragAccept ? styles.dropAccept : "",
                      isDragReject ? styles.dropReject : "",
                      isFocused ? styles.dropFocus : "",
                    )}
                  >
                    <div {...getRootProps()}>
                      <input {...getInputProps()} />
                      <div className={styles.bgimg}>
                        <FilesBg></FilesBg>
                      </div>
                      {!statement && (
                        <>
                          <p>{t("identity.dragAndDropSigned")}</p>
                          <br></br>
                          <p style={{ color: "#5d49c7", fontWeight: "500" }}>
                            {t("common.chooseFromDisk")}
                          </p>
                        </>
                      )}
                      {statement && isUploadingStatement && (
                        <div className={styles.verificaionInProgress}>
                          <Spinner className={styles.spinner} />
                          <p>{t("identity.verifyingStatement")}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className={classNames(styles.hint, styles.left)}>
                    {t("identity.downloadFileLink")}
                  </div>
                  {errorMessage && (
                    <div className={styles.warn}>{errorMessage}</div>
                  )}
                </>
              )}
            </>
          )}

        {method === IdentityMethod.Mobywatel &&
          step === IdentityStep.MObywatelQRCode && (
            <>
              <div className={styles.hint}>
                {!isMobile && <b>{t("identity.scanQRCode")}</b>}
                {isMobile && <b>{t("identity.verifyIdentityTitle")}</b>}
                <br></br>
                {!isMobile && (
                  <>
                    {t("identity.mobywatelDesktop")}
                  </>
                )}
                {isMobile && (
                  <>
                    {t("identity.mobywatelMobile")}
                  </>
                )}
                <br></br>
                {t("identity.requiredData")}
              </div>
              {!isMobile && <div id="qrcode" className={styles.qrcode}></div>}
              {isMobile && !isLoadingQRCode && (
                <div className={styles.center}>
                  <div className={styles.applink}>
                    <Button
                      text={t("identity.goToMobywatel")}
                      onClick={handleOpenmObywatel}
                    ></Button>
                  </div>
                  <div className={styles.or}>
                    {t("identity.copyCode")}
                  </div>
                  <div className={styles.keyBox}>
                    <div className={styles.key}>{key}</div>
                    <div
                      className={styles.copy}
                      onClick={() => navigator.clipboard.writeText(key)}
                    >
                      <Tooltip title={t("common.copy")}>
                        <div>
                          <CopyIcon></CopyIcon>
                        </div>
                      </Tooltip>
                    </div>
                  </div>
                </div>
              )}

              {isLoadingQRCode && (
                <div className={styles.loading}>
                  <Spinner className={styles.spinner} />
                  <div>{t("common.loading")}</div>
                </div>
              )}
            </>
          )}

        {step === IdentityStep.Success && (
          <>
            <div className={styles.subheader}>
              {t("identity.verificationSuccess")}
            </div>
            {/* <div className={styles.hint}>
            Weryfikacja Twoich danych przebiegła <b>pomyślnie</b>. Dane w Twoim
            profilu zostały uzupełnione o te uzyskane z procesu weryfikacji.
            <br></br>W przyszłości w przypadku zmiany imienia lub nazwiska
            konieczna będzie ponowna weryfikacja.
          </div>
          <div className={styles.subheader}>Uzyskane dane</div> */}
            <div className={styles.dataRow}>
              <Input
                disabled={true}
                value={personalData.firstName}
                RightIcon={
                  <div
                    className={classNames(styles.verification, styles.verified)}
                  >
                    {t("identity.verifiedData")}
                  </div>
                }
                label={t("forms.firstName")}
                onChange={() => {}}
              ></Input>
            </div>
            <div className={styles.dataRow}>
              <Input
                disabled={true}
                value={personalData.lastName}
                RightIcon={
                  <div
                    className={classNames(styles.verification, styles.verified)}
                  >
                    {t("identity.verifiedData")}
                  </div>
                }
                label={t("forms.lastName")}
                onChange={() => {}}
              ></Input>
            </div>
            <div className={styles.dataRow}>
              <Input
                disabled={true}
                value={personalData.pesel}
                RightIcon={
                  <div
                    className={classNames(styles.verification, styles.verified)}
                  >
                    {t("identity.verifiedData")}
                  </div>
                }
                label={t("forms.pesel")}
                onChange={() => {}}
              ></Input>
            </div>
          </>
        )}

        <div className={styles.buttons}>
          {/* {prevButtonState.show && (
            <HollowButton
              className={styles.button}
              text={prevButtonState.label}
              onClick={() => prevStepInternal?.()}
            ></HollowButton>
          )} */}
          {nextButtonState.show && (
            <Button
              className={styles.button}
              text={nextButtonState.label}
              disabled={nextButtonState.disabled}
              onClick={() => nextStepInternal?.()}
            ></Button>
          )}
        </div>
      </div>
    </>
  );
};

export default Identity;
